import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CancelToken, isCancel } from 'axios';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import PropTypes from 'prop-types';

import { REMOTE } from '../../Constants/RBAC';
import AssetService from '../../Services/AssetService';
import AssetUtils from '../../Lib/AssetUtils';
import AuthService from '../../Services/AuthService';
import CurrentUserContext from '../../Context/CurrentUserContext';
import { hasReadAccessToResource } from '../../RBAC/AccessTools';

import AssetDetailsTabDisplay from './AssetDetailsTabDisplay';
import AssetUpdateModal from './AssetUpdateModal';
import RawAPIModal from './RawAPIModal';
import UploadCustomerOTAModal from './UploadCustomerOTAModal';
import RunCustomerOTAModal from './RunCustomerOTAModal';
import DeleteCustomerOTAModal from './DeleteCustomerOTAModal';
import { otaTypes } from '../../Constants/OtaTypes';

export default memo(AssetDetailsTabContainer);

const initState = {
  activeSignals: 0,
  assetAddress: '',
  assetCoords: '',
  assetIsOnline: false,
  bridgeElevatURL: '',
  bridgeKey: '',
  bridgeKeyUpdatedTime: '',
  dateActivated: '',
  dateCreated: '',
  deviceStreamsElevatURL: '',
  deviceStreamsError: '',
  error: '',
  haveSystemAdminData: false,
  identityAssetIsOnline: false,
  identityConnectionStateUpdatedTime: '',
  identityLastActivityTime: '',
  identity2AssetIsOnline: false,
  identity2ConnectionStateUpdatedTime: '',
  identity2LastActivityTime: '',
  lastUpdated: '',
  modalIsOpen: false,
  onlineTime: '',
  otaPrintedData: '',
  packagesToUpdate: [],
  RawAPIData: '',
  RawAPIDataName: '',
  showRawAPIModal: false,
  showUploadCustomerOTAModal: false,
  showRunCustomerOTAModal: false,
  showDeleteCustomerOTAModal: false,
  twin: '',
  twin2: '',
  updatePending: false,
  updating: false,
  updateInProgress: false,
};

function AssetDetailsTabContainer(props) {
  const [state, updateState] = useState(initState);
  const currentUser = useContext(CurrentUserContext);

  const assetService = useMemo(() => new AssetService(), []);
  const assetUtils = useMemo(() => new AssetUtils(), []);
  const authService = useMemo(() => new AuthService(), []);

  const { assetID } = useParams();

  const source = CancelToken.source();
  const userHasRemoteRead = hasReadAccessToResource(props.asset?.orgID, REMOTE);

  const parseAssetConnectivity = (asset) => {
    return assetUtils.getConnectivityFromShadow(asset) === 'deviceConnect';
  };

  const fetchTwinsAndIdentities = () => {
    const twinRequest = assetService.fetchAssetTwin(assetID, source.token);
    const twin2Request = assetService.fetchAssetTwin2(assetID, source.token);
    const identityRequest = assetService.fetchAssetIdentity(
      assetID,
      source.token
    );
    const identity2Request = assetService.fetchAssetIdentity2(
      assetID,
      source.token
    );
    return Promise.all(
      [twinRequest, twin2Request, identityRequest, identity2Request].map((p) =>
        p.catch((e) => e)
      )
    );
  };

  const getDeviceStreamsKey = async () => {
    const deviceStreamsInfo = await assetService.fetchAssetDeviceStreamsInfo(
      assetID,
      source.token
    );
    return deviceStreamsInfo?.elevatURL ?? '';
  };

  const fetchRemoteData = async () => {
    const [twin, twin2, identity, identity2] = await fetchTwinsAndIdentities();
    const bridgeData = parseTwinDataForBridge(twin);

    const identityData = parseIdentityData(identity);
    const identity2Data = parseIdentity2Data(identity2);

    let ota;
    let customerOTAfromAPI;
    if (userHasRemoteRead) {
      try {
        ota = await assetService.fetchAssetOTA(assetID, source.token);
        customerOTAfromAPI = await assetService.fetchAssetCustomerOTAs(
          assetID,
          source.token
        );
      } catch (err) {
        // nothing
      }
    }

    const otaAndTwinData = parseOtaAndTwinData(ota, twin);
    const customerOTAlist = parseCustomerOTAdata(customerOTAfromAPI);

    return {
      ...bridgeData,
      ...identityData,
      ...identity2Data,
      ...otaAndTwinData,
      twin: twin?.twin ?? JSON.stringify({ error: twin?.message }),
      twin2: twin2?.twin ?? JSON.stringify({ error: twin2?.message }),
      customerOTAlist,
    };
  };

  const handleSubmitUpdate = async () => {
    try {
      updateState((prevState) => ({ ...prevState, updatePending: true }));
      const packageUpdateRequests = state.packagesToUpdate.map((blobName) => {
        return assetService.updateAssetSoftware(
          assetID,
          { blobName },
          source.token
        );
      });
      await Promise.all(packageUpdateRequests);
      updateState((prevState) => ({
        ...prevState,
        updating: true,
        updatePending: false,
      }));
    } catch (err) {
      if (isCancel(err)) return;

      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        updatePending: false,
        error,
      }));
    }
  };

  const parseCoordinates = (shadow) => {
    if (!shadow.gps) return '';

    const { latitude, longitude } = shadow.gps;
    const lat = assetUtils.parseGpsForDisplay(latitude, 'latitude') || '';
    const lng = assetUtils.parseGpsForDisplay(longitude, 'longitude') || '';
    return `${lat} ${lng}`;
  };

  const parseIdentityData = (identity) => {
    if (!identity || identity?.message) return {};

    const identityParsed = JSON.parse(identity?.identity || {});

    let identityConnectionStateUpdatedTime;
    if (identityParsed.connectionStateUpdatedTime) {
      const updatedTime = moment(identityParsed.connectionStateUpdatedTime);
      // consider times before this to be "never" - elevat was not running at this time
      if (updatedTime.isSameOrAfter(moment('20100101', 'YYYYMMDD'))) {
        identityConnectionStateUpdatedTime = `${updatedTime.toNow(true)} ago`;
      } else {
        identityConnectionStateUpdatedTime = 'never';
      }
    } else {
      identityConnectionStateUpdatedTime = 'unknown';
    }

    // this is updated on connection or packets being sent
    let identityLastActivityTime;
    if (identityParsed.lastActivityTime) {
      const updatedTime = moment(identityParsed.lastActivityTime);
      // consider times before this to be "never" - elevat was not running at this time
      if (updatedTime.isSameOrAfter(moment('20100101', 'YYYYMMDD'))) {
        identityLastActivityTime = `${updatedTime.toNow(true)} ago`;
      } else {
        identityLastActivityTime = 'never';
      }
    } else {
      identityLastActivityTime = 'unknown';
    }

    return {
      identityAssetIsOnline: identityParsed.connectionState === 'Connected', // "Disconnected" or "Connected"
      identityConnectionStateUpdatedTime,
      identityLastActivityTime,
    };
  };

  const parseIdentity2Data = (identity2) => {
    // copy of identity1 above
    if (!identity2 || identity2?.message) return {};

    const identity2Parsed = JSON.parse(identity2?.identity || {});

    let identity2ConnectionStateUpdatedTime;
    if (identity2Parsed.connectionStateUpdatedTime) {
      const updatedTime = moment(identity2Parsed.connectionStateUpdatedTime);
      // consider times before this to be "never" - elevat was not running at this time
      if (updatedTime.isSameOrAfter(moment('20100101', 'YYYYMMDD'))) {
        identity2ConnectionStateUpdatedTime = `${updatedTime.toNow(true)} ago`;
      } else {
        identity2ConnectionStateUpdatedTime = 'never';
      }
    } else {
      identity2ConnectionStateUpdatedTime = 'unknown';
    }

    // this is updated on connection or packets being sent
    let identity2LastActivityTime;
    if (identity2Parsed.lastActivityTime) {
      const updatedTime = moment(identity2Parsed.lastActivityTime);
      // consider times before this to be "never" - elevat was not running at this time
      if (updatedTime.isSameOrAfter(moment('20100101', 'YYYYMMDD'))) {
        identity2LastActivityTime = `${updatedTime.toNow(true)} ago`;
      } else {
        identity2LastActivityTime = 'never';
      }
    } else {
      identity2LastActivityTime = 'unknown';
    }

    return {
      identity2AssetIsOnline: identity2Parsed.connectionState === 'Connected', // "Disconnected" or "Connected"
      identity2ConnectionStateUpdatedTime,
      identity2LastActivityTime,
    };
  };

  const parseOtaAndTwinData = (ota, twin) => {
    if (!ota || !twin) return {};

    const otaPackages = JSON.parse(ota?.packages || {});
    const twinParsed = JSON.parse(twin?.twin || {});
    const twinDesiredPackages = twinParsed?.properties?.desired?.jobs ?? {};
    const twinReportedPackages = twinParsed?.properties?.reported?.jobs ?? {};
    const packagesToUpdate = Object.entries(otaPackages).reduce(
      (packages, [key, value = '']) => {
        const twinPackage =
          twinParsed?.properties?.reported?.softwareVersions?.[key] ?? '';
        return transformVal(twinPackage) === transformVal(value)
          ? packages
          : [...packages, value];
      },
      []
    );
    const desiredJobsKeys = Object.keys(twinDesiredPackages).sort();
    const reportedJobsKeys = Object.keys(twinReportedPackages).sort();
    const updateInProgress = !isEqual(desiredJobsKeys, reportedJobsKeys);

    const reportedVersions =
      twinParsed?.properties?.reported?.softwareVersions ?? {};

    return {
      updateInProgress,
      packagesToUpdate,
      otaPrintedData:
        'Packages to Update:\n' +
        JSON.stringify(packagesToUpdate, null, 2) +
        '\n\n' +
        'Installed Versions:\n' +
        JSON.stringify(reportedVersions, null, 2) +
        '\n\n' +
        'Twin Reported Jobs:\n' +
        JSON.stringify(twinReportedPackages, null, 2) +
        '\n\n' +
        'Twin Desired Jobs:\n' +
        JSON.stringify(twinDesiredPackages, null, 2),
    };
  };

  const parseCustomerOTAdata = (customerOTAfromAPI) => {
    if (!customerOTAfromAPI) return [];

    const parsed = JSON.parse(customerOTAfromAPI?.result || []);

    const output = parsed.map((ota) => {
      const createdOn = moment(ota.properties?.createdOn);
      const createdOnText = `${createdOn.toNow(true)}`;
      const typeName =
        otaTypes.find((otaType) => otaType.value == ota.type)?.label ||
        ota.type;

      let displayName = ota.filename;
      const MAX_FILENAME_DISPLAY_LENGTH = 40; // length based on what looks good in the grid on the details tab
      if (displayName.length > MAX_FILENAME_DISPLAY_LENGTH) {
        displayName =
          displayName.substring(0, MAX_FILENAME_DISPLAY_LENGTH - 3) + '...';
      }

      return {
        ...ota,
        typeName,
        displayName,
        createdOn: createdOnText,
      };
    });

    // sort newest to oldest
    output.sort((a, b) => {
      return moment(b.properties.createdOn) - moment(a.properties.createdOn);
    });

    return output;
  };

  const parseTwinDataForBridge = (twin) => {
    if (!twin) return {};

    const twinParsed = JSON.parse(twin?.twin || {});

    const bridgeKey = twinParsed?.properties?.reported?.key ?? ''; // looks like "QRDUOU6L"

    const bridgeKeyUpdatedTimeRaw =
      twinParsed?.properties?.reported?.$metadata?.key?.$lastUpdated; // looks like "2019-09-30T20:58:03.1424309Z"

    let bridgeKeyUpdatedTime;
    if (bridgeKeyUpdatedTimeRaw) {
      bridgeKeyUpdatedTime = `${moment(bridgeKeyUpdatedTimeRaw).toNow(
        true
      )} ago`;
    } else {
      bridgeKeyUpdatedTime = 'no bridge key';
    }

    const token = authService.getToken();
    const bridgeElevatURL = formatBridgeLink(currentUser._id, token, bridgeKey);

    return { bridgeElevatURL, bridgeKey, bridgeKeyUpdatedTime };
  };

  const setInitialState = async () => {
    const { asset } = props;

    const assetAddress = asset.shadow?.location || '--';
    const assetIsOnline = parseAssetConnectivity(asset);
    const onlineTime = assetUtils.getOnlineTimeFromShadow(asset);
    const lastUpdated = assetUtils.parseLastUpdatedForDetails(
      asset.shadow?.timestamp
    );
    const assetCoords = parseCoordinates(asset.shadow);
    const activeSignals = Array.isArray(asset.shadow?.signalCollections)
      ? assetUtils.getActiveSignalsFromShadow(asset)
      : 0;
    const dateCreated = assetUtils.getAssetCreatedAt(asset.createdAt);
    const dateActivated = assetUtils.getAssetActivatedOn(asset.activatedOn);

    if (!userHasRemoteRead) {
      return updateState((prevState) => ({
        ...prevState,
        activeSignals,
        assetAddress,
        assetCoords,
        assetIsOnline,
        dateActivated,
        dateCreated,
        onlineTime,
        lastUpdated,
      }));
    }

    let deviceStreamsElevatURL = state.deviceStreamsElevatURL;
    let deviceStreamsError = state.deviceStreamsError;

    try {
      deviceStreamsElevatURL = await getDeviceStreamsKey();
    } catch (err) {
      if (isCancel(err)) return;
      deviceStreamsError = err.response?.data?.message ?? err.message;
    }

    let remoteData = {};
    let error = state.error;
    try {
      remoteData = await fetchRemoteData();
    } catch (err) {
      if (isCancel(err)) return;
      error = err.response?.data?.message ?? err.message;
    }

    updateState((prevState) => ({
      ...prevState,
      ...remoteData,
      activeSignals,
      assetAddress,
      assetCoords,
      assetIsOnline,
      dateActivated,
      dateCreated,
      deviceStreamsElevatURL,
      deviceStreamsError,
      error,
      haveSystemAdminData: true,
      onlineTime,
      lastUpdated,
    }));
  };

  const toggleModal = () => {
    updateState((prevState) => ({
      ...prevState,
      modalIsOpen: !state.modalIsOpen,
    }));
  };

  const toggleRawAPIModal = (whichAPI) => {
    let RawAPIData;
    let RawAPIDataName;
    switch (whichAPI) {
      case 'twin':
        {
          let parsed;
          try {
            parsed = JSON.parse(state.twin || {});
          } catch (err) {
            parsed = { error: err.message };
          }
          RawAPIData = JSON.stringify(parsed, null, 4);
          RawAPIDataName = 'Main IoT Hub Twin';
        }
        break;
      case 'twin2':
        {
          let parsed;
          try {
            parsed = JSON.parse(state.twin2 || {});
          } catch (err) {
            parsed = { error: err.message };
          }
          RawAPIData = JSON.stringify(parsed, null, 4);
          RawAPIDataName = 'Alt IoT Hub Twin';
        }
        break;
      case 'OTAs':
        RawAPIData = state.otaPrintedData;
        RawAPIDataName = 'OTA Info';
        break;
      default:
        RawAPIData = '';
        RawAPIDataName = '';
        break;
    }

    updateState((prevState) => ({
      ...prevState,
      showRawAPIModal: !state.showRawAPIModal,
      RawAPIData,
      RawAPIDataName,
    }));
  };

  const toggleUploadCustomerOTAModal = () => {
    updateState((prevState) => ({
      ...prevState,
      showUploadCustomerOTAModal: !state.showUploadCustomerOTAModal,
    }));
  };

  const toggleRunCustomerOTAModal = (customerOTAselected) => {
    updateState((prevState) => ({
      ...prevState,
      showRunCustomerOTAModal: !state.showRunCustomerOTAModal,
      customerOTAselected,
    }));
  };

  const toggleDeleteCustomerOTAModal = (customerOTAselected) => {
    updateState((prevState) => ({
      ...prevState,
      showDeleteCustomerOTAModal: !state.showDeleteCustomerOTAModal,
      customerOTAselected,
    }));
  };

  const handleCustomerOTADeleted = (customerOTAselected) => {
    const filteredOTAs = state.customerOTAlist.filter(function (ota) {
      return (
        ota.type !== customerOTAselected.type ||
        ota.filename !== customerOTAselected.filename
      );
    });

    updateState((prevState) => ({
      ...prevState,
      customerOTAlist: filteredOTAs,
    }));
  };

  const handleCustomerOTAUpdated = async () => {
    let remoteData = {};
    let error = state.error;
    try {
      remoteData = await fetchRemoteData();
    } catch (err) {
      if (isCancel(err)) return;
      error = err.response?.data?.message ?? err.message;
    }

    updateState((prevState) => ({
      ...prevState,
      ...remoteData,
      error,
    }));
  };

  useEffect(() => {
    if (!props.asset?._id) return;
    setInitialState();
    return source.cancel;
  }, [props.asset?._id]);

  return (
    <div className="position-absolute container-fluid h-100 bg-white rounded py-4 d-flex flex-column overflow-auto">
      <AssetDetailsTabDisplay
        {...props}
        {...state}
        toggleModal={toggleModal}
        toggleRawAPIModal={toggleRawAPIModal}
        toggleUploadCustomerOTAModal={toggleUploadCustomerOTAModal}
        toggleRunCustomerOTAModal={toggleRunCustomerOTAModal}
        toggleDeleteCustomerOTAModal={toggleDeleteCustomerOTAModal}
      />
      {state.modalIsOpen ? (
        <AssetUpdateModal
          isOpen={state.modalIsOpen}
          toggleModal={toggleModal}
          handleSubmitUpdate={handleSubmitUpdate}
          updatePending={state.updatePending}
          updating={state.updating}
        />
      ) : null}
      {state.showRawAPIModal ? (
        <RawAPIModal
          data={state.RawAPIData}
          isOpen={state.showRawAPIModal}
          title={state.RawAPIDataName}
          toggleModal={toggleRawAPIModal}
        />
      ) : null}
      {state.showUploadCustomerOTAModal ? (
        <UploadCustomerOTAModal
          {...props}
          isOpen={state.showUploadCustomerOTAModal}
          handleCustomerOTAUpdated={handleCustomerOTAUpdated}
          toggleModal={toggleUploadCustomerOTAModal}
        />
      ) : null}
      {state.showRunCustomerOTAModal ? (
        <RunCustomerOTAModal
          {...props}
          customerOTAselected={state.customerOTAselected}
          isOpen={state.showRunCustomerOTAModal}
          toggleModal={toggleRunCustomerOTAModal}
        />
      ) : null}
      {state.showDeleteCustomerOTAModal ? (
        <DeleteCustomerOTAModal
          {...props}
          customerOTAselected={state.customerOTAselected}
          handleCustomerOTADeleted={handleCustomerOTADeleted}
          isOpen={state.showDeleteCustomerOTAModal}
          toggleModal={toggleDeleteCustomerOTAModal}
        />
      ) : null}
    </div>
  );
}

AssetDetailsTabContainer.propTypes = {
  asset: PropTypes.object,
  assetGroups: PropTypes.arrayOf(PropTypes.object),
  isRestrictedAssetForOtherThings: PropTypes.bool,
  otaHiddenForEdge: PropTypes.bool,
};

function formatBridgeLink(userID, token, bridgeKey) {
  return `elevat://Action=bridge;UserUUID=${userID};Token=${token};BridgeKey=${bridgeKey}`;
}

function transformVal(val) {
  return val?.split?.('/')?.pop?.();
}
